// YNX Music Promo Engine modal — legit organic growth pack for artists.
import React, { useState } from 'react';
import { X, Music, Download, Sparkles, Youtube, Calendar, Mail, Megaphone } from 'lucide-react';
import { generatePromo, downloadPromoZip } from '../lib/promo';
import { useToast } from './Toast';

export const PromoGenModal = ({ open, onClose }) => {
  const toast = useToast();
  const [songTitle, setSongTitle] = useState('');
  const [artistName, setArtistName] = useState('');
  const [genre, setGenre] = useState('rap français');
  const [mood, setMood] = useState('');
  const [audience, setAudience] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [streamingLinks, setStreamingLinks] = useState('');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('');
  const [result, setResult] = useState(null);

  if (!open) return null;

  const handleClose = () => {
    if (busy) return;
    setResult(null);
    onClose();
  };

  const handleGenerate = async () => {
    if (!songTitle.trim() || !artistName.trim()) {
      toast('Indique au moins le titre du morceau et ton nom d\'artiste', 'error');
      return;
    }
    setBusy(true);
    setResult(null);
    setStep('Analyse du genre & génération du plan complet (30-60s)…');
    try {
      const payload = await generatePromo({
        songTitle: songTitle.trim(),
        artistName: artistName.trim(),
        genre: genre.trim(),
        mood: mood.trim(),
        audience: audience.trim(),
        youtubeUrl: youtubeUrl.trim(),
        streamingLinks: streamingLinks.trim(),
      });
      setResult(payload);
      toast('Plan promo généré ! Télécharge le .zip ou consulte ci-dessous.', 'success');
    } catch (e) {
      console.error(e);
      toast(`Erreur : ${e.message}`, 'error');
    } finally {
      setBusy(false);
      setStep('');
    }
  };

  const handleDownload = async () => {
    if (!result) return;
    try {
      await downloadPromoZip(result);
      toast('Pack promo téléchargé 🎵', 'success');
    } catch (e) {
      toast(`Erreur ZIP : ${e.message}`, 'error');
    }
  };

  const plan = result?.plan || null;

  return (
    <div
      className='modal-overlay active'
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      data-testid='promo-overlay'
    >
      <div className='modal' style={{ maxWidth: 820 }}>
        <div className='modal-header'>
          <div className='modal-title' style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Music size={16} />
            <span>MUSIC PROMO ENGINE · CROISSANCE LÉGITIME</span>
          </div>
          <button className='modal-close' onClick={handleClose} disabled={busy} data-testid='promo-close'>
            <X size={14} />
          </button>
        </div>

        <div className='modal-body' style={{ maxHeight: '72vh', overflowY: 'auto' }}>
          {!result && (
            <>
              <p style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 18, lineHeight: 1.6 }}>
                Remplis les infos de ton morceau, YNX te livre un <b>pack promo complet</b> : SEO YouTube, captions Insta/TikTok/Twitter, 6 idées de shorts, EPK (press kit), liste de curateurs, email type, brief Ads, calendrier 30 jours et miniature 16:9.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className='modal-section' style={{ margin: 0 }}>
                  <label className='modal-label'>NOM D&apos;ARTISTE *</label>
                  <input type='text' name='promo-artist' className='hud-input' value={artistName} onChange={(e) => setArtistName(e.target.value)} placeholder='Ex: Ynyz' disabled={busy} data-testid='promo-artist' />
                </div>
                <div className='modal-section' style={{ margin: 0 }}>
                  <label className='modal-label'>TITRE DU MORCEAU *</label>
                  <input type='text' name='promo-song' className='hud-input' value={songTitle} onChange={(e) => setSongTitle(e.target.value)} placeholder='Ex: Lumière' disabled={busy} data-testid='promo-song' />
                </div>
              </div>

              <div className='modal-section' style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label className='modal-label'>GENRE</label>
                  <input type='text' name='promo-genre' className='hud-input' value={genre} onChange={(e) => setGenre(e.target.value)} placeholder='rap français, pop, électro…' disabled={busy} data-testid='promo-genre' />
                </div>
                <div>
                  <label className='modal-label'>MOOD / AMBIANCE</label>
                  <input type='text' name='promo-mood' className='hud-input' value={mood} onChange={(e) => setMood(e.target.value)} placeholder='énergique, mélancolique, dansant…' disabled={busy} data-testid='promo-mood' />
                </div>
              </div>

              <div className='modal-section'>
                <label className='modal-label'>AUDIENCE CIBLE</label>
                <textarea name='promo-audience' className='hud-input' value={audience} onChange={(e) => setAudience(e.target.value)} placeholder='18-30 ans, hip-hop urbain, France/Belgique, écoute Booba/Hamza…' rows={2} disabled={busy} data-testid='promo-audience' />
              </div>

              <div className='modal-section'>
                <label className='modal-label'>URL YOUTUBE DE TA VIDÉO</label>
                <input type='url' name='promo-yturl' className='hud-input' value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder='https://www.youtube.com/watch?v=…' disabled={busy} data-testid='promo-yturl' />
              </div>

              <div className='modal-section'>
                <label className='modal-label'>LIENS STREAMING (optionnel)</label>
                <textarea name='promo-streaming' className='hud-input' value={streamingLinks} onChange={(e) => setStreamingLinks(e.target.value)} placeholder='Spotify: …&#10;Apple Music: …&#10;Deezer: …' rows={3} disabled={busy} data-testid='promo-streaming' />
              </div>

              {busy && (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--cyan)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Sparkles size={12} className='spin' /> {step}
                </div>
              )}
            </>
          )}

          {result && plan && (
            <PromoResult plan={plan} thumbnailB64={result.thumbnail_b64} />
          )}
        </div>

        <div className='modal-footer'>
          {!result && (
            <>
              <button className='hud-btn' onClick={handleClose} disabled={busy}>ANNULER</button>
              <button className='hud-btn primary' onClick={handleGenerate} disabled={busy || !songTitle.trim() || !artistName.trim()} data-testid='promo-generate-btn'>
                {busy ? (<><Sparkles size={14} className='spin' /> GÉNÉRATION…</>) : (<><Sparkles size={14} /> GÉNÉRER LE PACK</>)}
              </button>
            </>
          )}
          {result && (
            <>
              <button className='hud-btn' onClick={() => setResult(null)}>← NOUVEAU</button>
              <button className='hud-btn primary' onClick={handleDownload} data-testid='promo-download-btn'>
                <Download size={14} /> TÉLÉCHARGER LE .ZIP
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div style={{ marginBottom: 22, paddingBottom: 18, borderBottom: '1px solid rgba(0,229,255,0.08)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: 'var(--cyan)', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: 1 }}>
      <Icon size={13} /> {title}
    </div>
    {children}
  </div>
);

const Pre = ({ children }) => (
  <div style={{ fontSize: 12, color: 'var(--text-bright)', lineHeight: 1.55, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{children}</div>
);

const PromoResult = ({ plan, thumbnailB64 }) => {
  const yt = plan.youtube_seo || {};
  const sp = plan.social_posts || {};
  const epk = plan.epk || {};
  const oe = plan.outreach_email || {};
  const ab = plan.ads_brief || {};

  return (
    <div data-testid='promo-result'>
      {thumbnailB64 && (
        <Section icon={Youtube} title='MINIATURE YOUTUBE'>
          <img src={`data:image/png;base64,${thumbnailB64}`} alt='thumbnail' style={{ width: '100%', maxWidth: 480, borderRadius: 6, border: '1px solid rgba(0,229,255,0.2)' }} />
        </Section>
      )}

      <Section icon={Youtube} title='SEO YOUTUBE'>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>TITRE</div>
        <Pre>{yt.title || '—'}</Pre>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 12, marginBottom: 4 }}>DESCRIPTION</div>
        <Pre>{yt.description || '—'}</Pre>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 12, marginBottom: 4 }}>TAGS ({(yt.tags || []).length})</div>
        <Pre>{(yt.tags || []).join(', ')}</Pre>
      </Section>

      <Section icon={Megaphone} title='CAPTIONS SOCIAL'>
        {['instagram', 'tiktok', 'twitter', 'facebook'].map((k) => (
          <div key={k} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4, textTransform: 'uppercase' }}>{k}</div>
            {(sp[k] || []).map((p, i) => (
              <Pre key={`${k}-${p.slice(0, 24)}-${i}`}>• {p}</Pre>
            ))}
          </div>
        ))}
        {sp.best_post_times && (
          <>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>HORAIRES OPTIMAUX</div>
            <Pre>{sp.best_post_times}</Pre>
          </>
        )}
      </Section>

      <Section icon={Sparkles} title='6 IDÉES DE SHORTS'>
        {(plan.shorts_ideas || []).map((s, i) => (
          <Pre key={`${s.hook || ''}-${i}`}>{`#${i + 1} (${s.duration || ''}) — ${s.hook}\n   → ${s.concept}`}</Pre>
        ))}
      </Section>

      <Section icon={Music} title='PRESS KIT'>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>BIO COURTE</div>
        <Pre>{epk.short_bio_fr || '—'}</Pre>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 12, marginBottom: 4 }}>BIO LONGUE</div>
        <Pre>{epk.long_bio_fr || '—'}</Pre>
      </Section>

      <Section icon={Mail} title='CURATEURS & EMAIL TYPE'>
        <Pre>{(plan.playlist_curators || []).map((c, i) => `${i + 1}. ${c.name} (${c.platform}) — ${c.niche} — via ${c.submit_via}`).join('\n')}</Pre>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 12, marginBottom: 4 }}>EMAIL TYPE</div>
        <Pre>{`Sujet : ${oe.subject || ''}\n\n${oe.body || ''}`}</Pre>
      </Section>

      <Section icon={Megaphone} title='BRIEF ADS LÉGITIMES'>
        <Pre>{`Plateforme : ${ab.platform || ''}\nBudget test : ${ab.budget_test || ''}\nAudience : ${ab.audience || ''}\nAccroche : ${ab.ad_copy_headline || ''}\nDescription : ${ab.ad_copy_description || ''}\nCPV attendu : ${ab.expected_cpv || ''}\nKPIs : ${(ab.kpi_to_track || []).join(', ')}`}</Pre>
      </Section>

      <Section icon={Calendar} title='CALENDRIER 30 JOURS'>
        <Pre>{(plan.content_calendar_30d || []).map((c) => `${c.day} · ${c.platform} (${c.format || ''}) — ${c.action}`).join('\n')}</Pre>
      </Section>

      <Section icon={Sparkles} title='3 ACTIONS POUR AUJOURD&apos;HUI'>
        <Pre>{(plan.daily_3_actions || []).map((a, i) => `${i + 1}. ${a}`).join('\n')}</Pre>
      </Section>
    </div>
  );
};
